import React, { useEffect, useState } from 'react';
import { useChatContext } from '@/hooks/useChatContext';
import { inboxService } from '@/services/inboxService';
import { supabase } from '@/integrations/supabase/client';
import { parseSchedulingConfig } from '@/types/scheduling.types';

// Add type declaration for Calendly global
declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

// Define Calendly event data structure
interface CalendlyEventData {
  event: {
    uri: string;
    start_time: string;
    end_time: string;
    [key: string]: unknown;
  };
  invitee: {
    uri: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface SchedulingWidgetProps {
  url?: string;
  height?: number;
  provider?: 'calendly' | 'google' | 'other';
  buttonText?: string;
}

export const SchedulingWidget: React.FC<SchedulingWidgetProps> = ({
  url,
  buttonText = 'Schedule a meeting',
  provider = 'calendly',
  height = 650,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isInlineOpen, setIsInlineOpen] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const context = useChatContext();

  // Add safety checks to prevent null reference errors
  if (!context) {
    return (
      <div className="w-full p-3 bg-red-50 text-red-600 border border-red-200 rounded-md my-2 text-sm">
        Chat context not available. Please refresh the page and try again.
      </div>
    );
  }

  const { agent, organization, agentChatUser, conversationId, handleSendMessage, calendlyMeta } = context;
  // Get setCalendlyMeta from context
  const setCalendlyMeta = context?.setCalendlyMeta;

  // Parse scheduling configuration safely
  const schedulingConfig = parseSchedulingConfig(agent?.skills_scheduling);

  // Check if agent has scheduling enabled
  if (!agent || !schedulingConfig?.enabled) {
    console.error('Scheduling widget rendered for agent without scheduling enabled');
    return (
      <div className="w-full p-3 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-md my-2 text-sm">
        Scheduling is not available for this agent. The administrator needs to enable and configure this feature.
      </div>
    );
  }

  // Use provided link or agent-level link with proper null/undefined handling
  const schedulingLink = url || schedulingConfig.scheduling_link || '';

  // Check if we have a valid scheduling link
  if (!schedulingLink) {
    return (
      <div className="w-full p-3 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-md my-2 text-sm">
        No scheduling link configured. Please contact the administrator.
      </div>
    );
  }

  const primaryColor = organization?.brand_color || '#4F46E5';
  const darkerColor = adjustColor(primaryColor, -20);

  // Load Calendly script once (popup widget)
  useEffect(() => {
    if (scriptLoaded || typeof window === 'undefined') return;

    const existing = document.getElementById('calendly-widget-script');
    if (existing) {
      setScriptLoaded(true);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.id = 'calendly-widget-style';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.id = 'calendly-widget-script';
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.body.appendChild(script);
  }, [scriptLoaded]);

  // Add check for potential missing data in calendlyMeta
  useEffect(() => {
    if (typeof window === 'undefined') return;
    console.log('[CAL-DBG] Component mounted/updated with calendlyMeta =', calendlyMeta);
  }, [calendlyMeta]);

  // Process a completed booking
  const processBookingCompletion = async (eventData: CalendlyEventData) => {
    console.log('[CALENDLY-FLOW] ⏺️ processBookingCompletion started');
    try {
      // Set booking complete state
      setBookingComplete(true);
      console.log('[CALENDLY-FLOW] ✅ setBookingComplete(true) called');

      // Extract important booking info
      const eventUri = eventData.event.uri;
      const inviteeUri = eventData.invitee.uri;
      const startTime = eventData.event.start_time;
      const endTime = eventData.event.end_time;

      console.log('[CALENDLY-FLOW] 📋 Extracted booking data:', { eventUri, inviteeUri, startTime, endTime });

      // COMMENTED OUT: Complex tool output relay logic - moving to direct tag approach
      /*
      // Two-path approach for robustness:
      if (!calendlyMeta?.tool_call_id || !calendlyMeta?.response_id) {
        console.warn("[CAL-DBG] No calendlyMeta – relay will NOT fire");
        console.log('[CALENDLY-FLOW] ⚠️ Missing required OpenAI IDs in calendlyMeta:', calendlyMeta);
        console.log('[CALENDLY-FLOW] 🔄 Falling back to manual continuation');

        // After short delay, continue conversation
        if (handleSendMessage) {
          console.log('[CALENDLY-FLOW] ⏱️ Setting timeout for manual message');
          setTimeout(() => {
            console.log('[CALENDLY-FLOW] 💬 Sending manual continuation message');
            handleSendMessage("I've completed my booking. The confirmation details have been sent to my email.");
          }, 1500);
        } else {
          console.log('[CALENDLY-FLOW] ❌ handleSendMessage not available, cannot continue manually');
        }
        return;
      }

      // If we have the OpenAI IDs, proceed with the relay
      console.log('[CALENDLY-FLOW] ✅ Valid calendlyMeta found:', calendlyMeta);
      const relayUrl = `${import.meta.env.VITE_SUPABASE_EDGE_URL ?? ''}/functions/v1/chat-tool-output`;
      console.log("[CAL-DBG] POST", relayUrl);
      console.log('[CALENDLY-FLOW] 🚀 Invoking edge function at URL:', relayUrl);

      try {
        console.log('[CALENDLY-FLOW] 📤 Sending payload to edge function:', {
          tool_call_id: calendlyMeta.tool_call_id,
          response_id: calendlyMeta.response_id,
          slotInfo: {
            status: "confirmed",
            event_uri: eventUri,
            invitee_uri: inviteeUri,
            start: startTime,
            end: endTime
          }
        });

        const { data, error } = await supabase.functions.invoke('chat-tool-output', {
          body: {
            tool_call_id: calendlyMeta.tool_call_id,
            response_id: calendlyMeta.response_id,
            slotInfo: {
              status: "confirmed",
              event_uri: eventUri,
              invitee_uri: inviteeUri,
              start: startTime,
              end: endTime
            }
          }
        });

        if (error) {
          console.error('❌ [CALENDLY-WIDGET] Error from relay:', error);
          console.log('[CALENDLY-FLOW] ❌ Edge function returned error:', error);
          // Fall back to manual continuation if relay fails
          if (handleSendMessage) {
            console.log('[CALENDLY-FLOW] 🔄 Falling back to manual message after relay error');
            setTimeout(() => {
              handleSendMessage("I've completed my booking. The confirmation details have been sent to my email.");
            }, 1000);
          }
        } else {
          console.log('✅ [CALENDLY-WIDGET] Tool output successfully posted to OpenAI');
          console.log('[CALENDLY-FLOW] ✅ Edge function success response:', data);
        }
      } catch (relayError) {
        console.error('[CALENDLY-FLOW] ❌ Exception during relay invocation:', relayError);
      }

      // Clear calendlyMeta to allow chat to resume
      console.log('[CALENDLY-WIDGET] Clearing calendlyMeta state to re-enable chat input');
      console.log('[CALENDLY-FLOW] 🧹 Clearing calendlyMeta state');
      if (setCalendlyMeta) {
        setCalendlyMeta(null);
        console.log('[CALENDLY-FLOW] ✅ setCalendlyMeta(null) called');
      } else {
        console.log('[CALENDLY-FLOW] ❌ setCalendlyMeta not available, could not clear state');
      }
      */

      // Simplified approach: Just send a manual continuation message
      if (handleSendMessage) {
        console.log('[CALENDLY-FLOW] ⏱️ Setting timeout for manual message');
        setTimeout(() => {
          console.log('[CALENDLY-FLOW] 💬 Sending manual continuation message');
          handleSendMessage("I've completed my booking. The confirmation details have been sent to my email.");
        }, 1500);
      } else {
        console.log('[CALENDLY-FLOW] ❌ handleSendMessage not available, cannot continue manually');
      }

    } catch (err) {
      console.error('Error during booking completion handling:', err);
      console.error('[CALENDLY-FLOW] ❌ Uncaught exception in processBookingCompletion:', err);
      // Ensure conversation can continue even if error occurs
      if (handleSendMessage) {
        console.log('[CALENDLY-FLOW] 🔄 Sending error recovery message');
        setTimeout(() => {
          handleSendMessage("I've completed my booking but there was a technical issue. The booking details were saved.");
        }, 1000);
      }
    } finally {
      console.log('[CALENDLY-FLOW] 🏁 processBookingCompletion completed');
    }
  };

  // Listen for Calendly booking completion events
  useEffect(() => {
    if (typeof window === 'undefined') return;

    console.log('[CALENDLY-DEBUG] Setting up message listener, calendlyMeta state:', calendlyMeta);

    const handleMessage = (e: MessageEvent) => {
      // Log all incoming messages to help debug
      console.log('[CALENDLY-DEBUG] Received message event from origin:', e.origin);

      if (e.origin !== 'https://calendly.com') {
        console.log('[CALENDLY-DEBUG] Ignoring message from non-Calendly origin');
        return;
      }

      try {
        // Log raw message data before parsing
        console.log('[CALENDLY-DEBUG] Raw message data:', e.data);

        // Handle both string JSON and object formats
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        console.log('[CALENDLY-DEBUG] Parsed message data:', data);
        console.log('[CALENDLY-DEBUG] Message event type:', data.event);

        if (data.event === 'calendly.event_scheduled') {
          // Log all available data for debugging
          console.log('Calendly booking completed!');
          console.log('Full event data:', data);
          console.log('Event URI:', data.payload.event.uri);
          console.log('Invitee URI:', data.payload.invitee.uri);
          console.log('[CALENDLY-DEBUG] Event payload details:', {
            event_uri: data.payload.event.uri,
            invitee_uri: data.payload.invitee.uri,
            start_time: data.payload.event.start_time,
            end_time: data.payload.event.end_time
          });
          console.log('[CALENDLY-DEBUG] Current calendlyMeta before processing:', calendlyMeta);

          // Process the completed booking
          processBookingCompletion(data.payload);
        } else {
          console.log('[CALENDLY-DEBUG] Received non-booking Calendly event:', data.event);
        }
      } catch (err) {
        console.error('[CALENDLY-DEBUG] Error processing Calendly message:', err);
        console.error('[CALENDLY-DEBUG] Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      console.log('[CALENDLY-DEBUG] Removing message listener');
      window.removeEventListener('message', handleMessage);
    };
  }, [conversationId, handleSendMessage, calendlyMeta]);

  const handleButtonClick = () => {
    if (!schedulingLink) {
      setError('No scheduling link available');
      return;
    }

    // Debug: Log available user information for pre-filling
    console.log('[CALENDLY-PREFILL] Available user data:', {
      name: agentChatUser?.name || 'not available',
      email: agentChatUser?.email || 'not available', 
      phone: agentChatUser?.phone || 'not available',
      agentChatUser: agentChatUser
    });

    // Prefer Calendly popup if script is loaded, else fallback to inline iframe
    if (scriptLoaded && window?.Calendly) {
      const params: Record<string, string> = {};
      if (agentChatUser?.name) params.name = agentChatUser.name;
      if (agentChatUser?.email) params.email = agentChatUser.email;
      if (agentChatUser?.phone) params.a1 = agentChatUser.phone; // Calendly uses 'a1' for phone

      const urlWithParams = Object.keys(params).length
        ? `${schedulingLink}${schedulingLink.includes('?') ? '&' : '?'}${new URLSearchParams(params).toString()}`
        : schedulingLink;

      console.log('[CALENDLY-PREFILL] Final URL with params:', urlWithParams);

      try {
        window.Calendly.initPopupWidget({ url: urlWithParams });
        return;
      } catch (e) {
        console.warn('Calendly popup failed, falling back to inline.', e);
      }
    }

    // Fallback
    setIsInlineOpen(true);
  };

  // Build embed URL for Calendly (for inline version)
  let embedUrl = schedulingLink;
  if (provider === 'calendly') {
    const params = new URLSearchParams();
    params.set('embed_domain', typeof window !== 'undefined' ? window.location.hostname : '');
    params.set('embed_type', 'Inline');

    // Prefill user info if available
    if (agentChatUser?.name) params.set('name', agentChatUser.name);
    if (agentChatUser?.email) params.set('email', agentChatUser.email);
    if (agentChatUser?.phone) params.set('a1', agentChatUser.phone); // Calendly uses 'a1' for phone

    embedUrl = `${schedulingLink}${schedulingLink.includes('?') ? '&' : '?'}${params.toString()}`;
    console.log('[CALENDLY-PREFILL] Inline embed URL with params:', embedUrl);
  }

  if (error) {
    return (
      <div className="w-full p-3 bg-red-50 text-red-600 border border-red-200 rounded-md my-2 text-sm">
        {error}
      </div>
    );
  }

  if (bookingComplete) {
    return (
      <div className="w-full p-4 my-4 bg-green-50 border border-green-200 rounded-md text-green-800">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="font-medium">Meeting scheduled successfully!</span>
        </div>
        <p className="mt-2 text-sm">
          You'll receive a confirmation email with all the details. We look forward to speaking with you!
        </p>
      </div>
    );
  }

  return (
    <div className="w-full my-4 flex flex-col items-center">
      {!isInlineOpen && (
        <button
          className="scheduling-button"
          onClick={handleButtonClick}
          style={{
            backgroundColor: primaryColor,
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
            textDecoration: 'none',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = darkerColor)}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = primaryColor)}
        >
          {buttonText}
        </button>
      )}

      {isInlineOpen && (
        <iframe
          src={embedUrl}
          title="Scheduling Widget"
          width="100%"
          height={height}
          frameBorder="0"
          className="mt-4 rounded-md border"
          allow="camera; microphone; fullscreen"
        ></iframe>
      )}
    </div>
  );
};

// Helper function to darken/lighten color
function adjustColor(color: string, amount: number): string {
  color = color.replace(/^#/, '');
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
} 